/** @jsx h */
import { Component, h, JSX } from 'preact';
import VideoOverlay from './video-overlay';
import cssStyles from './index.css';
import VASTError from '../lib/vast/vast-error';
import { isFeatureEnable } from '../../../common/js/features-enabling';
import { logMessage, stopRaven } from '../../../common/js/sentry';
import * as vpaid from '../../../common/js/vpaid/vpaid';
import Watermark from '../../../common/components/watermark';
import {
  isInterstitialAdUnit,
  isMediumRectangleAdUnit,
  isOptinVideoAdUnit
} from '../../../common/js/adUnit';
import {
  isRewardVideo,
  sendPauseTracks,
  sendResumeTracks
} from '../lib/actions';
import Blur from './video-blur';

import { VastIconType } from '../lib/vast/vast-icon';

import {
  sendOmidLoaded,
  sendOmidMediaEvent
} from '../../../common/js/omid/omid';
import {
  sendCreativeVideoError,
  sendCreativeVideoErrorAutoPlay
} from '../../../common/js/TrackAnalyticsHelper';
import { MediaErrorCodeConstants } from '../../../common/js/analytics/analytics.types';
import { isIos, isWeb } from '../../../common/js/environment-utils';
import VideoTag from './video-tag/video-tag';
import { EndScreenOrchestrator } from '../lib/end-screen/orchestrator';
import { VideoStyleStrategy } from '../lib/strategies/video-style/video-style-strategy';
import { getVideoStyleStrategy } from '../lib/strategies/video-style/video-style-strategy-factory';
import { RootStore } from '../../../common/js/stores/root.store';
import { VideoStore } from '../../../common/js/stores/video.store';
import { reaction } from '../../../common/js/utils/preact-utils';
import { ConfigStore } from '../../../common/js/stores/config.store';
import { MediaFile } from '../lib/vast/media-file';

import { CreativeVendorRegistry } from '../../../common/js/vendors/creative-vendor.registry';
import { trackRealVideoComplete } from '../lib/video-progress-service';

const NetworkState = {
  NetworkEmpty: 0,
  NetworkIdle: 1,
  NetworkLoading: 2,
  NetworkNoSource: 3
};

const OPEN_AUCTION = 'marketplace';

export interface VideoProps {
  containerId: string;
  config: FormatConfig;
  context: AdContext;
  actions: Record<string, () => void>;
  vastIcon?: VastIconType;
  canInstallIcon: boolean;
  onError: (e: Error | VASTError) => void;
  onImpression: () => void;
  onToggleMute: (isMuted: boolean) => void;
  onClose: () => void;
  onOptout: () => void;
  onForceClose: () => void;
  onPause: () => void;
  onResume: () => void;
  onResize: (width: number, height: number) => void;
  onVideoPlayerReady: (videoPlayer: HTMLVideoElement) => void;
  onAdStarted: () => void;
  onComplete: () => void;
  onSkip: (shouldComplete?: boolean) => void;
  onStopped: () => void;
  onVolumeChanged: () => void;
  onClick: (url?: string, source?: string, handledByPlayer?: boolean) => void;
}

interface VideoState {
  isVideoMuted: boolean;
  isVideoBuffering: boolean;
  elapsedTime: number;
  videoReady: boolean;
  videoDuration: number;
}

const blackImage =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';

export class Video extends Component<VideoProps, VideoState> {
  VIDEO_START_TIMEOUT: number = 10000;
  videoStartHandle?: number;
  wasPreviouslyPaused: boolean;
  ignoreVideoElementErrors: boolean = false;
  isAutoClosing: boolean = false;

  videoState: Record<string, number> = {};
  videoStore: VideoStore;
  videoStyleStrategy: VideoStyleStrategy;
  configStore: ConfigStore;

  constructor(props: VideoProps) {
    super(props);

    this.onVideoLoadStart = this.onVideoLoadStart.bind(this);
    this.onVideoProgress = this.onVideoProgress.bind(this);
    this.onVideoComplete = this.onVideoComplete.bind(this);
    this.onVideoStart = this.onVideoStart.bind(this);
    this.onVideoError = this.onVideoError.bind(this);
    this.onVideoBufferingStart = this.onVideoBufferingStart.bind(this);
    this.onVideoBufferingStop = this.onVideoBufferingStop.bind(this);
    this.onToggleMute = this.onToggleMute.bind(this);
    this.onVideoReady = this.onVideoReady.bind(this);
    this.onOptout = this.onOptout.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
    this.onVideoClose = this.onVideoClose.bind(this);
    this.onVideoAutoClose = this.onVideoAutoClose.bind(this);
    this.onOverlaySkip = this.onOverlaySkip.bind(this);
    this.onSkipVideo = this.onSkipVideo.bind(this);

    this.wasPreviouslyPaused = false;

    const rootStore = RootStore.getInstance();
    this.videoStore = rootStore.videoStore;
    this.configStore = rootStore.configStore;

    this.state = {
      isVideoMuted: !props.config.video?.soundOnStart,
      isVideoBuffering: false,
      elapsedTime: 0,
      videoReady: false,
      videoDuration: props.config.mediaFiles[0].duration
    };

    this.registerVpaidEvents();

    this.videoStyleStrategy = getVideoStyleStrategy(
      this.videoStore,
      this.configStore
    );
  }

  get isVpaid(): boolean {
    return this.videoStore.isVpaid;
  }

  setupStoreObservers(): void {
    const destroyVideoStartedReaction = reaction(
      () => this.videoStore.hasVideoStarted.value,
      (hasVideoStarted: boolean) => {
        if (!hasVideoStarted) return;

        this.onVideoStart();
        destroyVideoStartedReaction && destroyVideoStartedReaction();
      }
    );

    reaction(
      () => this.videoStore.shouldPlayVideo.value,
      (shouldPlayVideo: boolean) => {
        if (this.videoStore.isVideoCompleted.value) return;

        if (this.videoStore.hasVideoStarted.value)
          return this.handlePlayAndPause(shouldPlayVideo);

        if (!shouldPlayVideo) return;

        this.startVideo();
      },
      true
    );
  }

  handlePlayAndPause(shouldPlayVideo: boolean): void {
    if (shouldPlayVideo) {
      this.handleResumeAndTrack();
    } else {
      this.handlePauseAndTrack();
    }
  }

  registerVpaidEvents(): void {
    if (!this.isVpaid) {
      return;
    }

    vpaid.addEventListener('AdSkipped', this.props.onSkip);
    vpaid.addEventListener('AdPlaying', this.props.onResume);
    vpaid.addEventListener('AdPaused', this.props.onPause);
    vpaid.addEventListener('AdVolumeChange', this.props.onVolumeChanged);
    vpaid.addEventListener('AdStarted', () => {
      this.videoStore.setVideoStarted();
      this.props.onAdStarted();
    });
    vpaid.addEventListener('AdImpression', this.props.onImpression);
    vpaid.addEventListener('AdVideoComplete', this.onVideoComplete);
    vpaid.addEventListener('AdStopped', () => {
      const hasEndScreen =
        !isMediumRectangleAdUnit(this.props.context.adUnitType) &&
        EndScreenOrchestrator.instance.hasNext;

      if (!hasEndScreen || this.isAutoClosing) {
        this.props.onStopped();
      }
    });
  }

  handlePauseAndTrack(): void {
    this.handlePause();

    if (!this.wasPreviouslyPaused) {
      sendPauseTracks();
      this.wasPreviouslyPaused = true;
    }
  }

  handlePause(): void {
    this.isVpaid && vpaid.pauseAd();
    !this.isVpaid &&
      this.videoStore.videoPlayer.value &&
      this.videoStore.videoPlayer.value.pause();
  }

  handleResume(): void {
    this.isVpaid && vpaid.resumeAd();
    !this.isVpaid &&
      this.videoStore.videoPlayer.value &&
      this.videoStore.videoPlayer.value.play();
  }

  handleResumeAndTrack(): void {
    this.handleResume();
    if (this.wasPreviouslyPaused) {
      sendResumeTracks();
      this.wasPreviouslyPaused = false;
    }
  }

  componentDidMount(): void {
    this.setupStoreObservers();

    if (this.videoStore.videoPlayer.value) {
      this.props.onVideoPlayerReady(this.videoStore.videoPlayer.value);
      window.addEventListener('resize', this.onWindowResize);

      this.listenMwebVideoControlDispatch();
    } else {
      logMessage(
        'Unable to call setVideoElementToContext. The video player ref has not been loaded.'
      );
    }
  }

  componentWillUnmount(): void {
    stopRaven();
    this.stopVideoStartWatch();
    window.removeEventListener('resize', this.onWindowResize);
  }

  listenMwebVideoControlDispatch(): void {
    if (isWeb()) {
      RootStore.getInstance().adContainerStore.adContainerPort.addEventListener(
        'dispatchVideoControl',
        (event: string) => {
          if (event === 'play') {
            this.handleResumeAndTrack();
          }
        }
      );
    }
  }

  onWindowResize(): void {
    const width =
      (document &&
        document.documentElement &&
        document.documentElement.clientWidth) ||
      window.innerWidth;
    const height =
      (document &&
        document.documentElement &&
        document.documentElement.clientHeight) ||
      window.innerHeight;

    if (this.videoStore.shouldDisplaySkin()) {
      this.props.onResize(width, width * (9 / 16));
    } else {
      this.props.onResize(width, height);
    }

    this.videoStore.updateWindowSize({ width, height });
  }

  startVideo(): void {
    if (this.isVpaid) {
      !vpaid.isAdStarted() && vpaid.startAd(this.videoStore.videoPlayer.value!);
      return;
    }

    requestAnimationFrame(() => {
      const playPromise = this.videoStore.videoPlayer.value!.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          sendCreativeVideoErrorAutoPlay();
        });
      }
    });

    if (isIos()) {
      this.onWindowResize();
    }
  }

  onVideoReady(): void {
    this.videoState['onVideoReady'] = 1;

    const skipOffset =
      this.props.config.closeBtn && this.props.config.closeBtn.displayAfter;

    sendOmidLoaded({
      isSkippable: (skipOffset || 0) < this.state.videoDuration,
      skipOffset: skipOffset || 0,
      isAutoPlay: true,
      position: 'standalone'
    });
  }

  onOverlaySkip(): void {
    this.ignoreVideoElementErrors = true;
    this.isAutoClosing = true;
    this.props.onSkip();
  }

  onVideoClose(): void {
    this.ignoreVideoElementErrors = true;
    this.props.actions.onClose();
  }

  onVideoAutoClose(): void {
    this.isAutoClosing = true;
    this.onVideoClose();
  }

  onVideoError(): void {
    if (this.ignoreVideoElementErrors) {
      return;
    }

    const getDiagnosticVideoSources = (states: Record<string, number>) => {
      const videoElement = document.getElementById(
        'video'
      ) as HTMLVideoElement | null;

      if (!videoElement) {
        return {};
      }

      const sources = document.querySelector('#video > source');

      const sourceSrcs: Array<{ src: string; type: string }> = [];

      if (sources) {
        const sourceElements = document.querySelectorAll('#video > source');
        for (let i = 0; i < sourceElements.length; i++) {
          const sourceEl = sourceElements[i] as HTMLSourceElement;
          sourceSrcs.push({
            src: sourceEl.src,
            type: sourceEl.type
          });
        }
      }

      return {
        videoSrc: videoElement.src,
        videoSourcesSrcs: sourceSrcs,
        readyState: videoElement.readyState,
        networkState: videoElement.networkState,
        currentSrc: videoElement.currentSrc,
        currentTime: videoElement.currentTime,
        crossOrigin: videoElement.crossOrigin,
        states
      };
    };

    const videoPlayer = this.videoStore.videoPlayer.value;
    if (!videoPlayer) return;

    const { error, networkState } = videoPlayer;

    let sentryErrorDetails = null;
    let creativeError = null;
    let errorObj: Error | VASTError | null = null;
    if (error) {
      sentryErrorDetails = {
        errorCode: error.code,
        errorMessage: error.message
      };
      creativeError = error;
      errorObj = new VASTError(405, 'Problem displaying a media file');
      (errorObj as VASTError).additionalTrace = {
        videoPlayerErrorCode: error.code,
        videoPlayerErrorMsg: error.message,
        isVpaid: this.isVpaid,
        vpaidState: vpaid.getVpaidState(),
        mediaFiles: this.props.config.mediaFiles,
        playerDiag: {
          ...getDiagnosticVideoSources(this.videoState)
        }
      };
    } else if (networkState === NetworkState.NetworkNoSource) {
      sentryErrorDetails = {
        errorCode: NetworkState.NetworkNoSource,
        errorMessage: `Video Network State Error: Cannot fetch video from provided URL`
      };

      creativeError = {
        code: MediaErrorCodeConstants.MEDIA_ERR_NETWORK
      };

      errorObj = new Error('Video No Source Error');
      (errorObj as any).additionalTrace = {
        videoPlayerNetworkErrorCode: sentryErrorDetails.errorMessage,
        videoPlayerNetworkErrorMsg: sentryErrorDetails.errorMessage,
        isVpaid: this.isVpaid,
        vpaidState: vpaid.getVpaidState(),
        mediaFiles: this.props.config.mediaFiles,
        playerDiag: {
          ...getDiagnosticVideoSources(this.videoState)
        }
      };
    }

    if (creativeError && sentryErrorDetails && errorObj) {
      sendCreativeVideoError(creativeError as MediaError);
      logMessage('HTML5 Video player error', sentryErrorDetails);
      this.props.onError(errorObj);
    }
  }

  startVideoStartWatch(): void {
    this.videoStartHandle = window.setTimeout(() => {
      this.props.onError(
        new VASTError(
          405,
          `Video failed to start playing after ${this.VIDEO_START_TIMEOUT /
            1000} seconds`
        )
      );
    }, this.VIDEO_START_TIMEOUT);
  }

  stopVideoStartWatch(): void {
    this.videoStartHandle && clearTimeout(this.videoStartHandle);
  }

  onVideoLoadStart(): void {
    this.videoState['onVideoLoadStart'] = 1;
    if (!isFeatureEnable('videoFormatLegacyPlaybackError')) {
      return;
    }

    this.startVideoStartWatch();
  }

  onVideoProgress() {
    if (!this.videoStore.videoPlayer.value) {
      return;
    }

    // Get progress from our video element
    let progress = this.videoStore.videoPlayer.value.currentTime;

    // Let vendor override progress if active (e.g., Connected Stories has its own player)
    progress = CreativeVendorRegistry.getProgress(progress);

    // Mark video as started when we get actual progress
    if (progress > 0 && !this.videoStore.hasVideoStarted.value) {
      this.videoStore.setVideoStarted();
    }

    // Track progress and check if completion should be triggered
    let shouldComplete = false;
    if (progress) {
      shouldComplete = this.videoStore.onVideoProgress(progress);
    }

    // Trigger completion when video reaches billed duration
    if (shouldComplete && !this.isVpaid) {
      this.props.onComplete();
    }

    // Handle auto-restart prevention (depends on component state/methods)
    const shouldStopAutoRestart =
      parseInt((progress as unknown) as string) === 0 &&
      parseInt((this.state.elapsedTime as unknown) as string) >=
        parseInt((this.state.videoDuration as unknown) as string) &&
      !EndScreenOrchestrator.instance.endScreen &&
      !EndScreenOrchestrator.instance.hasNext;

    if (shouldStopAutoRestart) {
      if (!this.videoStore.isVideoCompleted.value) {
        !this.isVpaid && this.props.onComplete();
      }
      // prevent loop
      !this.ignoreVideoElementErrors && this.onVideoClose();
    }

    if (progress) {
      this.setState({
        elapsedTime: progress
      });
    }
  }

  onSkipVideo(): void {
    const { config, context, onComplete, onSkip } = this.props;
    if (!this.videoStore.isVideoCompleted.value && context.isUserAcquisition) {
      sendOmidMediaEvent('skipped');
      if (isRewardVideo() && config.video && config.video.sendTracksOnClick) {
        onComplete();
      }
    }

    this.videoStore.setVideoCompleted();
    onSkip(false);
  }

  onVideoComplete(): void {
    this.videoStore.setVideoCompleted();
    this.props.onComplete();
    trackRealVideoComplete();
  }

  onVideoStart(): void {
    this.stopVideoStartWatch();

    !this.isVpaid && this.props.onAdStarted();
    !this.isVpaid && this.props.onImpression();
  }

  onVideoBufferingStart(): void {
    this.videoState['onVideoBufferingStart'] = 1;

    this.setState({
      isVideoBuffering: true
    });

    if (this.videoStore.hasVideoStarted.value) {
      sendOmidMediaEvent('bufferStart');
    }
  }

  onVideoBufferingStop(): void {
    this.videoState['onVideoBufferingStop'] = 1;
    if (this.state.isVideoBuffering) {
      if (this.videoStore.hasVideoStarted.value) {
        sendOmidMediaEvent('bufferFinish');
      }

      this.setState({
        isVideoBuffering: false
      });
    }
  }

  onToggleMute(): void {
    this.props.onToggleMute(!this.state.isVideoMuted);
    this.setState({
      isVideoMuted: !this.state.isVideoMuted
    });
  }

  onOptout(): void {
    this.props.onOptout();
  }

  getStyles() {
    return this.videoStyleStrategy.getStyles();
  }

  isInterstitialAdUnit(): boolean {
    return (
      this.props.context &&
      (isInterstitialAdUnit(this.props.context.adUnitType) ||
        isOptinVideoAdUnit(this.props.context.adUnitType))
    );
  }

  render(): JSX.Element {
    const { containerId, config } = this.props;

    const shouldDisplayBlur = this.videoStore.shouldDisplayBlur.value;
    const styles = this.getStyles();

    const onVideoComplete = !this.isVpaid ? this.onVideoComplete : () => null;
    const shouldInjectVideoSources = !this.isVpaid && config.mediaFiles;
    const mediaFiles = config.mediaFiles.filter(
      (o: MediaFile) => o.type !== 'application/javascript'
    );

    return (
      <div style={styles.container} id={containerId}>
        <div className={cssStyles.videoContainer} style={styles.videoContainer}>
          {config.skin?.skinTop && <div className={cssStyles.skinTop} />}
          {!this.videoStore.isVideoCompleted.value && (
            <VideoTag
              preload={config.video?.preload}
              poster={!config.video?.enableOpacityOnShow ? blackImage : null}
              wrapperStyle={styles.videoWrapper}
              videoStyle={styles.video}
              vpaidContainerStyle={styles.vpaidContainer}
              muted={this.state.isVideoMuted}
              mediaFiles={shouldInjectVideoSources ? mediaFiles : undefined}
              onVideoBufferingStop={this.onVideoBufferingStop}
              onVideoBufferingStart={this.onVideoBufferingStart}
              onVideoComplete={onVideoComplete}
              onVideoProgress={this.onVideoProgress}
              onVideoLoadStart={this.onVideoLoadStart}
              onVideoError={this.onVideoError}
              onVideoReady={this.onVideoReady}
            />
          )}

          {shouldDisplayBlur && (
            <Blur videoPlayer={this.videoStore.videoPlayer.value!} />
          )}
          {config.skin?.skinBottom && <div className={cssStyles.skinBottom} />}
        </div>

        {(this.videoStore.hasVideoStarted.value ||
          (window as any).isOgyPreview) && (
          <VideoOverlay
            {...config}
            {...(this.props.actions as any)}
            canInstallIcon={this.props.canInstallIcon}
            onClick={(
              source?: string,
              event?: MouseEvent | MessageEvent,
              url?: string,
              handledByPlayer?: boolean
            ) => {
              this.props.onClick(url, source, handledByPlayer);
            }}
            elapsedTime={this.state.elapsedTime}
            onOptout={this.onOptout}
            onForceClose={this.props.onForceClose}
            onClose={this.onVideoClose}
            onAutoClose={this.onVideoAutoClose}
            isVideoMuted={this.state.isVideoMuted}
            videoDuration={this.state.videoDuration}
            onToggleSound={this.onToggleMute}
            adUnit={this.props.context.adUnitType}
            mediaFile={config.mediaFiles[0]}
            showOptoutLink={this.props.context.showOptoutLink}
            vastIcon={this.props.vastIcon}
            childPrivacyApplied={this.props.context.childPrivacyApplied}
            skip={this.onOverlaySkip}
            onSkipVideo={this.onSkipVideo}
            skanActivated={this.props.context.skan || false}
            isOA={this.props.context.family === OPEN_AUCTION}
          />
        )}
        {this.isInterstitialAdUnit() &&
        !this.props.context.childPrivacyApplied ? (
          <Watermark
            position="bottom-left"
            left={this.props.context.showOptoutLink ? 70 : 16}
            bottom={16}
            locale={config.locale}
            watermarkApiKeys={config.watermarkApiKeys}
            apiKey={this.props.context.apiKey}
          />
        ) : (
          ''
        )}
      </div>
    );
  }
}

export default Video;
